import { useCallback, useMemo, useRef } from 'react';
import { useFormContext } from 'react-hook-form';
import { useParams } from 'react-router-dom';

import { DIMENSIONALITY_VALUES } from 'constants/businessTool';
import { FOR_BANK_SECTIONS, FOR_BANK_SUB_SECTIONS } from 'constants/businessToolSections/forBank';
import { useFinancialWarnings } from 'contexts/FinancialWarningsContext';
import { debounce } from 'lodash';
import { useGetPpeAssetsCalculations } from 'services/calculations/balanceSheet';

import useCheckSubSectionAccess from 'hooks/useCheckSubSectionAccess';

import { initialPeriodsTableRow, IPeriodsTableRow } from 'components/BusinessToolForm/constants/periods';
import {
  applyRunningBalanceFloor,
  capProceedsByAvailableBalance,
  getOpeningPpeBalanceForProceeds,
  isOpeningBalanceAssetsEmpty,
} from 'components/BusinessToolForm/utils/financialCalculations';
import {
  PERIODS_OPERATION_TYPES,
  summarizeSubtractPeriodValues,
} from 'components/BusinessToolForm/utils/helpers';

import ToastService from 'helpers/ToastService';

import { IFormContentTypes, observedFields } from '../../utils/form';

export const usePpeAssetsCalculation = () => {
  const { id: businessPlanId = '' } = useParams();
  const { setValue } = useFormContext();

  const getPpeAssetsCalculation = useGetPpeAssetsCalculations();
  const {
    setExcessiveProceedsFromSalePeriods,
    ppeBalanceBeforeProceeds,
    setPpeBalanceBeforeProceeds,
  } = useFinancialWarnings();
  const ppeBalanceBeforeProceedsRef = useRef(ppeBalanceBeforeProceeds);
  ppeBalanceBeforeProceedsRef.current = ppeBalanceBeforeProceeds;

  const canAccess = useCheckSubSectionAccess(
    FOR_BANK_SECTIONS.MARKETING_AND_SALES,
    FOR_BANK_SUB_SECTIONS.FOR_BANK_MARKETING_AND_SALES.SALES_PLAN,
  );

  const [ppeFiledName, intangibleFiledName] = observedFields.ppeAssets.setCalculatedValueFieldNames;

  const debouncedUpdateFunction = useMemo(
    () =>
      debounce(async (ppeAssetsData, unit, rawProceedsFromSale: IPeriodsTableRow) => {
        const params = {
          unit,
          businessPlanId,
          ppeAssetsData,
        };

        try {
          const withoutProceeds = await getPpeAssetsCalculation.mutateAsync({
            ...params,
            ppeAssetsData: { ...ppeAssetsData, proceedsFromSale: initialPeriodsTableRow },
          });

          setPpeBalanceBeforeProceeds(withoutProceeds.propertyPlantAndEquipment);

          const { effective: cappedProceeds, excessivePeriodKeys } = capProceedsByAvailableBalance(
            rawProceedsFromSale,
            withoutProceeds.propertyPlantAndEquipment,
          );

          setExcessiveProceedsFromSalePeriods(excessivePeriodKeys);

          const { intangibleAssets, propertyPlantAndEquipment } = await getPpeAssetsCalculation.mutateAsync({
            ...params,
            ppeAssetsData: { ...ppeAssetsData, proceedsFromSale: cappedProceeds },
          });

          setValue(
            ppeFiledName,
            applyRunningBalanceFloor(propertyPlantAndEquipment, ppeAssetsData.propertyPlantAndEquipment),
          );
          setValue(intangibleFiledName, applyRunningBalanceFloor(intangibleAssets, ppeAssetsData.intangibleAssets));
        } catch (error) {
          if (error instanceof Error) ToastService.showError(error.message);
        }
      }, 500),

    [],
  );

  const ppeAssetsCalculationHandler = useCallback(
    async (content: IFormContentTypes['ppeAssets']) => {
      const [
        propertyPlantAndEquipment,
        intangibleAssets,
        investingActivitiesTable,
        depreciation,
        amortization,
        fundingDirectionTable,
        operationUnit,
        gainsAndLossesAssets,
      ] = content;

      const [purchaseOfIntangibleAndOtherNonCurrentAssets, , governmentGrants] = [
        investingActivitiesTable[0],
        investingActivitiesTable[2],
        investingActivitiesTable[4],
      ];
      const rawProceedsFromSale = investingActivitiesTable[2] as unknown as IPeriodsTableRow;
      const [construction, equipmentPurchase] = [fundingDirectionTable[3], fundingDirectionTable[4]];
      const gainsAndLosses = summarizeSubtractPeriodValues(
        [gainsAndLossesAssets, governmentGrants],
        PERIODS_OPERATION_TYPES.ADD,
        true,
      );

      const openingBalanceIsEmpty = isOpeningBalanceAssetsEmpty(propertyPlantAndEquipment, intangibleAssets);
      const openingPpe = Number(propertyPlantAndEquipment) || 0;
      const availableBalanceForWarnings =
        ppeBalanceBeforeProceedsRef.current ?? getOpeningPpeBalanceForProceeds(openingPpe);

      if (openingPpe > 0 || ppeBalanceBeforeProceedsRef.current) {
        const { excessivePeriodKeys } = capProceedsByAvailableBalance(rawProceedsFromSale, availableBalanceForWarnings);
        setExcessiveProceedsFromSalePeriods(excessivePeriodKeys);
      } else {
        setExcessiveProceedsFromSalePeriods(new Set());
      }

      if (!canAccess) return;

      const ppeAssetsData = {
        propertyPlantAndEquipment: openingPpe,
        intangibleAssets: Number(intangibleAssets),
        purchaseOfIntangibleAndOtherNonCurrentAssets,
        depreciation: openingBalanceIsEmpty ? initialPeriodsTableRow : depreciation,
        amortization: openingBalanceIsEmpty ? initialPeriodsTableRow : amortization,
        construction,
        equipmentPurchase,
        proceedsFromSale: rawProceedsFromSale,
        gainsAndLosses,
      };
      const unit = operationUnit ?? DIMENSIONALITY_VALUES.SINGLE;

      debouncedUpdateFunction(ppeAssetsData, unit, rawProceedsFromSale);
    },

    [canAccess, debouncedUpdateFunction, ppeFiledName, setExcessiveProceedsFromSalePeriods, setPpeBalanceBeforeProceeds],
  );

  return ppeAssetsCalculationHandler;
};
